import { Badge, ButtonBase, Divider, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { makeStyles } from 'tss-react/mui';
import { useUserData } from 'hooks/use-user-data';
import AccountMenu from './account-menu';
import { getActiveSection, NAV_RAIL_WIDTH, NavSection, useNavSections } from './nav-config';

interface RailItemProps {
  section: NavSection;
  active: boolean;
  onClick(): void;
}

const RailItem: React.FC<RailItemProps> = ({ section, active, onClick }) => {
  const { classes, cx } = useStyles();

  return (
    <ButtonBase className={cx(classes.item, active && classes.itemActive, section.disabled && classes.itemDisabled)} onClick={onClick}>
      <span className={classes.itemIcon}>
        <Badge badgeContent={section.badge} color={'error'} max={99} className={classes.itemBadge}>
          {section.icon}
        </Badge>
      </span>
      <Typography className={classes.itemLabel}>{section.label}</Typography>
    </ButtonBase>
  );
};

const NavRail: React.FC = () => {
  const { classes, cx } = useStyles();
  const location = useLocation();
  const navigate = useNavigate();
  const sections = useNavSections();
  const { firstName, lastName } = useUserData();

  const [accountAnchor, setAccountAnchor] = useState<HTMLElement | null>(null);

  const activeSection = getActiveSection(sections, location.pathname);
  const railSections = sections.filter((s) => !s.hiddenInRail);
  const topSections = railSections.filter((s) => !s.bottom);
  const bottomSections = railSections.filter((s) => s.bottom);

  const initials = `${firstName?.[0] ?? ''}${lastName?.[0] ?? ''}`.toUpperCase() || 'U';

  const handleClick = (section: NavSection) => {
    if (section.disabled) return;
    navigate(section.route);
  };

  return (
    <nav className={classes.rail} id="pai-nav-rail">
      <img className={classes.logo} src="/dashboard/images/logos/product_logo.svg" alt="SAIA" />
      {topSections.map((section) => (
        <RailItem key={section.key} section={section} active={activeSection?.key === section.key} onClick={() => handleClick(section)} />
      ))}
      <div className={classes.spacer} />
      {bottomSections.map((section) => (
        <RailItem key={section.key} section={section} active={activeSection?.key === section.key} onClick={() => handleClick(section)} />
      ))}
      <Divider className={classes.divider} />
      <ButtonBase
        className={cx(classes.item, (Boolean(accountAnchor) || activeSection?.key === 'account') && classes.itemActive)}
        onClick={(event) => setAccountAnchor(event.currentTarget)}
      >
        <Stack className={classes.avatar}>{initials}</Stack>
        <Typography className={classes.itemLabel}>Account</Typography>
      </ButtonBase>
      <AccountMenu anchorEl={accountAnchor} onClose={() => setAccountAnchor(null)} />
    </nav>
  );
};

const useStyles = makeStyles()((theme) => ({
  rail: {
    position: 'fixed',
    top: 0,
    left: 0,
    bottom: 0,
    width: `${NAV_RAIL_WIDTH}px`,
    boxSizing: 'border-box',
    background: '#F5F5FB',
    borderRight: '1px solid #E3E3E8',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '12px 0 10px 0',
    gap: '2px',
    zIndex: 1200,
    overflowY: 'auto',
    overflowX: 'hidden',
  },
  logo: {
    width: '28px',
    height: '28px',
    marginBottom: '12px',
    flex: 'none',
  },
  item: {
    width: '60px',
    padding: '7px 0',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '3px',
    color: '#555555',
    flex: 'none',
    '&:hover': {
      background: 'rgba(255,255,255,0.7)',
    },
  },
  itemActive: {
    background: '#FFFFFF',
    boxShadow: '0 4px 12px 0 rgba(0,0,0,0.08)',
    color: theme.palette.primary.main,
    '& p': {
      fontWeight: 700,
    },
    '&:hover': {
      background: '#FFFFFF',
    },
  },
  itemDisabled: {
    opacity: 0.4,
    cursor: 'default',
  },
  itemIcon: {
    display: 'flex',
    '& svg': {
      fontSize: theme.typography.pxToRem(22),
    },
  },
  itemBadge: {
    '& .MuiBadge-badge': {
      fontSize: theme.typography.pxToRem(10),
      height: '16px',
      minWidth: '16px',
      padding: '0 4px',
    },
  },
  itemLabel: {
    fontSize: theme.typography.pxToRem(9),
    fontWeight: 500,
    textAlign: 'center',
    lineHeight: 1.15,
    color: 'inherit',
  },
  spacer: {
    flex: 1,
    minHeight: '12px',
  },
  divider: {
    width: '40px',
    margin: '6px 0',
  },
  avatar: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #5044FF, #4AA4FF)',
    color: '#FFF',
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export default NavRail;
